'use client';
// ──────────────────────────────────────────────────────
// Idle Screen — PRD §3.1
// Attract screen with global barcode scanner + product overlay
// Scanner captures barcode → calls scan API → shows product card
// Tapping anywhere also navigates to /scan (manual mode).
// ──────────────────────────────────────────────────────
import { useCallback, useState, useEffect, useSyncExternalStore } from 'react';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { AiProductShowcase } from '@/components/AiProductShowcase';
import { ChatDrawer } from '@/components/ChatDrawer';
import { ProductCardOverlay } from '@/components/ProductCardOverlay';
import { ScanDebugPanel } from '@/components/ScanDebugPanel';
import { AiCubeHero } from '@/components/AiCubeHero';
import { DeviceGate } from '@/components/DeviceGate';
import { AiScanLoader } from '@/components/AiScanLoader';
import { MobileQrBlock } from '@/components/MobileQrBlock';
import { PublicFooter } from '@/components/PublicFooter';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { useTwoPhaseScan } from '@/hooks/useTwoPhaseScan';
import { useHeartbeat } from '@/hooks/useHeartbeat';
import { checkHealth } from '@/lib/api';
import { resolveIdentity } from '@/lib/kiosk-identity';

// ── SSR-safe auth store (useSyncExternalStore) ─────────
type AuthSnapshot = { provisioned: boolean; gateReason: 'unprovisioned' | 'revoked' };
const AUTH_DEFAULT: AuthSnapshot = { provisioned: true, gateReason: 'unprovisioned' };
let authCurrent: AuthSnapshot = AUTH_DEFAULT;
let authReady = false;
const authSubs = new Set<() => void>();
function authSubscribe(cb: () => void) { authSubs.add(cb); return () => { authSubs.delete(cb); }; }
function getAuthSnapshot(): AuthSnapshot {
  if (!authReady && typeof window !== 'undefined') {
    authReady = true;
    authCurrent = { ...AUTH_DEFAULT, provisioned: resolveIdentity().provisioned };
  }
  return authCurrent;
}
function getAuthServerSnapshot(): AuthSnapshot { return AUTH_DEFAULT; }
function setAuth(next: AuthSnapshot) { authCurrent = next; authSubs.forEach(l => l()); }
// ────────────────────────────────────────────────────────

export default function IdlePage() {
  const router = useRouter();
  const [backendStatus, setBackendStatus] = useState<'checking' | 'ok' | 'error'>('checking');
  const [chatOpen, setChatOpen] = useState(false);
  // SSR-safe identity — no setState in effect (useSyncExternalStore)
  const authState = useSyncExternalStore(authSubscribe, getAuthSnapshot, getAuthServerSnapshot);
  const gateVisible = !authState.provisioned;

  // Heartbeat — sends every 30s for provisioned devices, clears credentials on 401
  useHeartbeat({
    enabled: authState.provisioned && !chatOpen,
    onUnauthorized: () => {
      setAuth({ provisioned: false, gateReason: 'revoked' });
    },
  });

  // Listen for auth-revoked events from chat/scan 401 handlers
  useEffect(() => {
    const handler = () => {
      setChatOpen(false);
      setAuth({ provisioned: false, gateReason: 'revoked' });
    };
    window.addEventListener('kiosk-auth-revoked', handler);
    return () => window.removeEventListener('kiosk-auth-revoked', handler);
  }, []);

  // ── Two-phase scan (shared hook) ───────────────────
  const {
    scanData, scanLoading, scanError, scanPhase,
    overlayOpen, closeScan, debugInfo,
  } = useTwoPhaseScan();

  // ── Backend connectivity probe ──────────────────────
  useEffect(() => {
    checkHealth().then((ok) => setBackendStatus(ok ? 'ok' : 'error'));
  }, []);

  // ── Activate barcode scanner ───────────────────────
  // Scanner → navigate to chat with product context
  const handleBarcodeScan = useCallback((barcode: string) => {
    router.push(`/m/q?barcode=${encodeURIComponent(barcode)}`);
  }, [router]);

  useBarcodeScanner({
    onScan: handleBarcodeScan,
    enabled: !chatOpen,
  });

  // ── Tap anywhere → /scan (manual mode) ─────────────
  const handleTap = useCallback(() => {
    // Don't navigate if product card, chat, or device gate is showing
    if (overlayOpen || scanLoading || chatOpen || gateVisible) return;
    router.push('/scan');
  }, [router, overlayOpen, scanLoading, chatOpen, gateVisible]);

  return (
    <main
      onClick={handleTap}
      className="min-h-dvh flex flex-col items-center px-4 sm:px-6 lg:px-8 relative overflow-hidden cursor-pointer bg-white"
    >
      {/* Device Gate — shown for unprovisioned or revoked devices */}
      {gateVisible && (
        <DeviceGate
          reason={authState.gateReason}
          onProvisioned={() => {
            setAuth({ provisioned: true, gateReason: 'unprovisioned' });
          }}
        />
      )}

      {/* AI Scan Loader — shown during ai_processing phase */}
      <AiScanLoader visible={scanPhase === 'ai_processing'} />

      {/* Product Card Overlay */}
      <ProductCardOverlay
        data={scanData}
        loading={scanLoading}
        error={scanError}
        scanPhase={scanPhase}
        onDismiss={closeScan}
      />

      {/* Chat Drawer */}
      <div onClick={e => e.stopPropagation()}>
        <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
      </div>

      {/* ── HERO CONTAINER (max-width + centered) ───────── */}
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center gap-6">

        {/* ── QIO LOGO (wide, premium, responsive) ────────── */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mt-10 sm:mt-14"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/branding/qio-logo.jpeg"
            alt="QIO"
            className="w-[220px] sm:w-[260px] md:w-[320px]"
            style={{
              height: 'auto',
              maxHeight: '90px',
              objectFit: 'contain',
              borderRadius: '16px',
              filter: 'drop-shadow(0 4px 14px rgba(14, 165, 165, 0.18))',
            }}
          />
        </motion.div>

        {/* ── SCAN RINGS HERO (centerpiece animation) ─────── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
          style={{ minHeight: '180px' }}
          className="flex items-center justify-center"
        >
          <AiCubeHero />
        </motion.div>

        {/* ── HEADLINE (Hijazi) ─────────────────────────── */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center"
          style={{
            fontSize: 'clamp(1.5rem, 4.5vw, 2.5rem)',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.35,
            maxWidth: '680px',
            color: '#1a1a2e',
          }}
        >
          <span className="gradient-text">كيو</span>.. ذكاء يقرأ احتياجك ويختصر مشوارك!
        </motion.h1>

        {/* ── SUBTEXT ─────────────────────────────────── */}
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center"
          style={{
            color: '#475569',
            fontSize: 'clamp(0.95rem, 2.2vw, 1.15rem)',
            maxWidth: '540px',
            lineHeight: 1.7,
          }}
        >
          أول مستشار تجميل بالذكاء الاصطناعي في المملكة.. يعطيك الزبدة والترشيحات اللي تفرق معاك، بسرعة وببساطة. ✨
        </motion.p>

        {/* Mobile Q Entry — QR block in hero flow */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="w-full mt-4"
          onClick={e => e.stopPropagation()}
        >
          <MobileQrBlock />
        </motion.div>

        {/* ── WHITE CARD TAGLINE ─────────────────────── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="w-full max-w-md mx-auto rounded-2xl px-6 py-5 text-center"
          style={{
            background: '#f8fafc',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
        >
          <p style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '1.05rem', marginBottom: '6px' }}>
            إيش حكاية هالمنتج؟
          </p>
          <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.65 }}>
            لقطة وحدة للباركود دحين، وخذي علم المنتج: فوايده، مكوناته، وطريقة استخدامه بالتفصيل اللي يريح بالك.
          </p>
        </motion.div>

        {/* Promo Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.55 }}
          className="w-full mt-8"
        >
          <AiProductShowcase />
        </motion.div>


        {/* "سولف مع كيو" CTA — centered below card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.65 }}
          className="w-full flex justify-center mt-8 mb-10"
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={() => setChatOpen(true)}
            className="flex items-center gap-3 px-10 py-4 rounded-full font-bold text-base transition-all active:scale-95 hover:brightness-110"
            style={{
              background: 'linear-gradient(135deg, #0ea5a5 0%, #0d8f8f 100%)',
              color: '#ffffff',
              boxShadow: '0 6px 28px rgba(14, 165, 165, 0.35), 0 2px 8px rgba(0,0,0,0.08)',
              minHeight: '56px',
              fontSize: '1.1rem',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
            سولف مع كيو
          </button>
        </motion.div>

      </div>{/* END HERO CONTAINER */}

      {/* Public footer — admin link, legal pages, Baynatec credit.
          Wrapper stops click propagation so the kiosk's tap-anywhere → /scan
          handler doesn't hijack footer links. PublicFooter itself stays
          handler-free so it can render inside Server Components (/privacy,
          /policy) without violating the App Router boundary. */}
      <div className="w-full mt-auto" onClick={(e) => e.stopPropagation()}>
        <PublicFooter />
      </div>

      {/* AI Ready badge */}
      <div className="fixed bottom-4 right-4 z-50 pointer-events-none">
        <div className="ai-ready-badge px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-md border border-[var(--color-border-accent)] bg-[var(--color-glass)] text-[var(--color-accent)]">
          AI Ready ✦
        </div>
      </div>

      {/* Debug Panel (toggle with D key) */}
      <ScanDebugPanel info={debugInfo} />

      {/* Backend connectivity badge */}
      <div className="fixed bottom-4 left-4 z-50 pointer-events-none">
        <div className={`px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-md border ${backendStatus === 'ok'
          ? 'bg-green-50 border-green-300 text-green-700'
          : backendStatus === 'error'
            ? 'bg-red-50 border-red-300 text-red-700'
            : 'bg-neutral-50 border-neutral-200 text-neutral-400'
          }`}>
          {backendStatus === 'ok' && 'Backend Connected ✅'}
          {backendStatus === 'error' && 'Backend Not Reachable ❌'}
          {backendStatus === 'checking' && 'Checking...'}
        </div>
      </div>
    </main>
  );
}
